import {
  FileText,
  LoaderCircle,
} from 'lucide-react'
import {
  useEffect,
  useRef,
  useState,
} from 'react'

import {
  buscarFacturaExistente,
  registrarFacturaProveedor,
} from '../../services/abastecimiento'
import {
  TIPOS_DOCUMENTO_FACTURA,
  calcularVencimientoFactura,
  mensajeErrorFactura,
  sumarMontosFactura,
  validarFactura,
} from '../../utils/abastecimiento'
import {
  formatearMontoCompra,
  nombreProveedorCompra,
} from '../../utils/compras'
import { normalizarMoneda } from '../../utils/numeros'
import {
  formatearFechaPos,
  obtenerFechaActualPos,
} from '../../utils/pos'
import ModalBase, {
  AvisoModal,
  CampoModal,
  estiloInputModal,
} from './ModalBase'

function CampoMonto({
  id,
  label,
  valor,
  onChange,
  deshabilitado,
  requerido = false,
}) {
  return (
    <CampoModal
      label={label}
      htmlFor={id}
      requerido={requerido}
    >
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-semibold text-slate-400">
          $
        </span>

        <input
          id={id}
          type="text"
          inputMode="decimal"
          value={valor}
          onChange={(event) => onChange(event.target.value)}
          placeholder="0"
          disabled={deshabilitado}
          className={`${estiloInputModal} pl-7`}
        />
      </div>
    </CampoModal>
  )
}

function FacturaModal({
  abierto,
  compras,
  empresaId,
  onCerrar,
  onRegistrada,
  onVerGasto,
}) {
  const [tipoDocumento, setTipoDocumento] = useState('factura')
  const [numero, setNumero] = useState('')
  const [fechaFactura, setFechaFactura] = useState(obtenerFechaActualPos())
  const [condicion, setCondicion] = useState('CONTADO')
  const [plazoDias, setPlazoDias] = useState('')
  const [neto, setNeto] = useState('')
  const [exento, setExento] = useState('')
  const [iva, setIva] = useState('')
  const [otros, setOtros] = useState('')
  const [total, setTotal] = useState('')

  const [procesando, setProcesando] = useState(false)
  const [error, setError] = useState('')
  const [facturaExistente, setFacturaExistente] = useState(null)
  const enCursoRef = useRef(false)

  useEffect(() => {
    if (!abierto) return

    setTipoDocumento('factura')
    setNumero('')
    setFechaFactura(obtenerFechaActualPos())
    setCondicion('CONTADO')
    setPlazoDias('')
    setNeto('')
    setExento('')
    setIva('')
    setOtros('')
    setTotal('')
    setError('')
    setFacturaExistente(null)
    enCursoRef.current = false
  }, [abierto])

  if (!abierto || !compras || compras.length === 0) return null

  const proveedor = compras[0]

  // Referencia: suma de las compras seleccionadas (monto definitivo).
  const sumaCompras = compras.reduce(
    (suma, compra) =>
      suma + Number(compra.monto_logistico_registrado || 0),
    0
  )

  const totalIngresado = normalizarMoneda(total)
  const diferencia = Number.isFinite(totalIngresado)
    ? Math.round((totalIngresado - sumaCompras) * 100) / 100
    : null

  const sumaComponentes = sumarMontosFactura({
    neto,
    exento,
    iva,
    otros,
  })

  const vencimiento =
    condicion === 'CRÉDITO' && /^\d+$/.test(plazoDias.trim())
      ? calcularVencimientoFactura(fechaFactura, Number(plazoDias))
      : null

  const alRegistrar = async () => {
    if (enCursoRef.current) return

    const { error: errorValidacion, valores } = validarFactura({
      tipoDocumento,
      numeroDocumento: numero,
      fechaFactura,
      condicion,
      plazoDias,
      neto,
      exento,
      iva,
      otros,
      total,
    })

    if (errorValidacion) {
      setError(errorValidacion)
      return
    }

    enCursoRef.current = true
    setProcesando(true)
    setError('')
    setFacturaExistente(null)

    try {
      const facturaId = await registrarFacturaProveedor({
        empresaId,
        proveedorId: proveedor.proveedor_id,
        tipoDocumento: valores.tipoDocumento,
        numero: valores.numero,
        fechaFactura: valores.fechaFactura,
        condicion: valores.condicion,
        plazo: valores.plazo,
        neto: valores.neto,
        exento: valores.exento,
        iva: valores.iva,
        otros: valores.otros,
        total: valores.total,
        compraIds: compras.map((compra) => compra.id),
      })

      onRegistrada(facturaId)
    } catch (errorRegistro) {
      console.error('Error al registrar factura:', errorRegistro)

      const mensaje = mensajeErrorFactura(errorRegistro)

      setError(mensaje)

      // Documento duplicado: permitir consultar el existente en vez de
      // reintentar (la RPC jamás duplica factura ni gasto).
      if (/ya existe/i.test(mensaje)) {
        try {
          const existente = await buscarFacturaExistente({
            empresaId,
            proveedorId: proveedor.proveedor_id,
            tipoDocumento: valores.tipoDocumento,
            numero: valores.numero,
          })

          setFacturaExistente(existente || { sinFactura: true })
        } catch (errorBusqueda) {
          console.error('Error al buscar factura existente:', errorBusqueda)
        }
      }
    } finally {
      enCursoRef.current = false
      setProcesando(false)
    }
  }

  return (
    <ModalBase
      abierto={abierto}
      titulo="Registrar factura"
      tituloId="factura-titulo"
      procesando={procesando}
      onCerrar={onCerrar}
    >
      <p className="mt-2 text-sm text-slate-500">
        {nombreProveedorCompra(proveedor)} · {compras.length}{' '}
        {compras.length === 1 ? 'compra' : 'compras'} (
        {compras.map((compra) => compra.codigo_compra).join(', ')})
      </p>

      <div className="mt-5 space-y-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <CampoModal
            label="Tipo de documento"
            htmlFor="factura-tipo"
            requerido
          >
            <select
              id="factura-tipo"
              value={tipoDocumento}
              onChange={(event) => setTipoDocumento(event.target.value)}
              disabled={procesando}
              className={estiloInputModal}
            >
              {TIPOS_DOCUMENTO_FACTURA.map((tipo) => (
                <option
                  key={tipo.valor}
                  value={tipo.valor}
                >
                  {tipo.etiqueta}
                </option>
              ))}
            </select>
          </CampoModal>

          <CampoModal
            label="Número"
            htmlFor="factura-numero"
            requerido
          >
            <input
              id="factura-numero"
              type="text"
              value={numero}
              onChange={(event) => setNumero(event.target.value)}
              maxLength={100}
              disabled={procesando}
              className={estiloInputModal}
            />
          </CampoModal>

          <CampoModal
            label="Fecha de factura"
            htmlFor="factura-fecha"
            requerido
          >
            <input
              id="factura-fecha"
              type="date"
              value={fechaFactura}
              onChange={(event) => setFechaFactura(event.target.value)}
              disabled={procesando}
              className={estiloInputModal}
            />
          </CampoModal>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <CampoModal
            label="Condición de pago"
            htmlFor="factura-condicion"
            requerido
          >
            <select
              id="factura-condicion"
              value={condicion}
              onChange={(event) => {
                setCondicion(event.target.value)
                setPlazoDias('')
              }}
              disabled={procesando}
              className={estiloInputModal}
            >
              <option value="CONTADO">Contado</option>
              <option value="CRÉDITO">Crédito</option>
            </select>
          </CampoModal>

          {condicion === 'CRÉDITO' && (
            <CampoModal
              label="Plazo (días corridos)"
              htmlFor="factura-plazo"
              requerido
            >
              <input
                id="factura-plazo"
                type="text"
                inputMode="numeric"
                value={plazoDias}
                onChange={(event) => setPlazoDias(event.target.value)}
                placeholder="30"
                disabled={procesando}
                className={estiloInputModal}
              />

              {vencimiento && (
                <p className="mt-2 text-sm text-slate-500">
                  Vence el {formatearFechaPos(vencimiento)}
                </p>
              )}
            </CampoModal>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <CampoMonto
            id="factura-neto"
            label="Neto"
            valor={neto}
            onChange={setNeto}
            deshabilitado={procesando}
          />

          <CampoMonto
            id="factura-exento"
            label="Exento"
            valor={exento}
            onChange={setExento}
            deshabilitado={procesando}
          />

          <CampoMonto
            id="factura-iva"
            label="IVA"
            valor={iva}
            onChange={setIva}
            deshabilitado={procesando}
          />

          <CampoMonto
            id="factura-otros"
            label="Otros impuestos"
            valor={otros}
            onChange={setOtros}
            deshabilitado={procesando}
          />

          <CampoMonto
            id="factura-total"
            label="Total factura"
            valor={total}
            onChange={setTotal}
            deshabilitado={procesando}
            requerido
          />
        </div>

        {sumaComponentes !== null &&
          Number.isFinite(totalIngresado) &&
          sumaComponentes !== totalIngresado && (
            <AvisoModal tipo="aviso">
              Neto + exento + IVA + otros suman{' '}
              {formatearMontoCompra(sumaComponentes)}; el total debe
              coincidir.
            </AvisoModal>
          )}

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Referencia (solo informativa)
          </p>

          <div className="flex justify-between gap-4">
            <span className="text-slate-500">Suma de las compras</span>
            <span className="font-semibold tabular-nums text-slate-900">
              {formatearMontoCompra(sumaCompras)}
            </span>
          </div>

          {diferencia !== null && (
            <div className="mt-1 flex justify-between gap-4">
              <span className="text-slate-500">Diferencia con la factura</span>
              <span className="font-semibold tabular-nums text-slate-900">
                {formatearMontoCompra(diferencia)}
              </span>
            </div>
          )}

          <p className="mt-2 text-xs text-slate-500">
            El gasto usa los montos reales de la factura. La diferencia no
            modifica compras ni inventario.
          </p>
        </div>

        {error && <AvisoModal>{error}</AvisoModal>}

        {facturaExistente && !facturaExistente.sinFactura && (
          <button
            type="button"
            onClick={() => onVerGasto(facturaExistente.gasto_id)}
            className="flex min-h-12 w-full items-center justify-center gap-2 rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-100"
          >
            <FileText size={18} />
            Ver documento existente en Gastos
          </button>
        )}

        {facturaExistente?.sinFactura && (
          <AvisoModal tipo="info">
            El documento está en Gastos (registrado manualmente). Búscalo
            en el módulo Gastos; no se creó un duplicado.
          </AvisoModal>
        )}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCerrar}
            disabled={procesando}
            className="min-h-12 w-full rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-60 sm:w-auto"
          >
            Cancelar
          </button>

          <button
            type="button"
            onClick={alRegistrar}
            disabled={procesando}
            className="flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700 disabled:bg-emerald-400 sm:w-auto"
          >
            {procesando && (
              <LoaderCircle
                size={18}
                className="animate-spin"
              />
            )}
            {procesando ? 'Registrando...' : 'Registrar factura'}
          </button>
        </div>
      </div>
    </ModalBase>
  )
}

export default FacturaModal
